import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import ModuleGate from '../ModuleGate';
import FederationGate from '../FederationGate';
import NotFound from '../NotFound';
import { getUserData } from '../../utils/getUserInfo';
import { onSessionExpired } from '../../utils/sessionEvents';
import { rememberAttemptedUrl } from '../../utils/attemptedUrl';
import './Layout.css';
import AppFooter from '@/components/Layout/AppFooter';
import { AppPageHeading, PageHeadingBar } from '@/components/Layout/AppPageHeading';
import { PageHeadingProvider } from '@/components/Layout/PageHeadingContext';
import { AppHeaderBreadcrumbs } from '@/components/EaShell/AppHeaderBreadcrumbs';
import "react-toastify/dist/ReactToastify.css";
import i18n from '../../i18n';
import ProtectedRoute from '../ProtectedRoute';
import { isLoginPath, isPublicPath } from '../../utils/authPaths';
import RoleBasedRoute from '../RoleBasedRoute';
import Notification from '../Notification/Notification';
// import RcsaCatalogCompliance from '@/pages/RCSACompliance/rcsa';  // route commented out
import Rcsa from 'src/pages/RCSA/rcsa';
import ExportSurvey from '@/pages/AllSurveys/ExportSurvey';
// import TicketsSupCompliance from '@/pages/Tickets_Sup_Compliance/Tickets';  // route commented out
// import StandardsSupCompliance from '@/pages/references_Sup_Compliance/references';  // route commented out
// import CertificationsListSup from '@/pages/certifications_Sup_compliance/ListCertifications';  // route commented out
// import SurveyCategoryQuestionManager from '@/pages/SurveyCategoryQuestion/SurveyCategoryQuestion';  // route commented out
// import ViewQuestionsTable from '@/pages/QuestionAssessment_Sup_Comly/ViewQuestionAssessment';  // route commented out
// import CreateQuestionForm from '@/pages/QuestionAssessment_Sup_Comly/createQuestionAssessment';  // route commented out

// Lazy-loaded page components (code splitting to reduce initial bundle size)
const IntegrationSettingsPage = React.lazy(() => import('src/pages/Configuration/IntegrationSettingsPage'));
const DataSheetsPage = React.lazy(() => import('src/pages/Configuration/DataSheetsPage'));
const ComplianceStatus = React.lazy(() => import('../../pages/compliance/complianceStatus/compliance_status'));
const UserManage = React.lazy(() => import('src/pages/User/UserManage'));
const UserActivation = React.lazy(() => import('src/pages/User/UserActivation'));
const UserPermissionManage = React.lazy(() => import('src/pages/User/UserPermissionManage'));
const PermissionGroupPageManage = React.lazy(() => import('src/pages/Permission/PermissionGroupPageManage'));
// const TaskUserCalender = React.lazy(() => import('src/pages/DashboardImplementor/component/TaskUserCalender'));  // route commented out below
// const TenantTickets = React.lazy(() => import('src/pages/Tenant/TenantTickets'));
// const TenantSettings = React.lazy(() => import('src/pages/Tenant/TenantSettings'));
// const TenantStandards = React.lazy(() => import('src/pages/Tenant/TenantStandards'));
// const TenantDetail = React.lazy(() => import('src/pages/Tenant/TenantDetail'));
// Phase 4 — Tenant Owner Admin (subtree management, memberships, roll-ups)
// Pianat Admin (platform-operator console) was extracted into its own standalone
// app (pianat_admin_frontend) so it is never shipped to customer browsers.
// TrustLens — light-tier onboarding (Posture Cascade L1→L2→L3→L4)
const TrustLensPostureLayout = React.lazy(() => import('src/pages/TrustLens/PostureContext'));
const TrustLensPostureL1 = React.lazy(() => import('src/pages/TrustLens/PostureL1Page'));
const TrustLensPostureL2 = React.lazy(() => import('src/pages/TrustLens/PostureL2Page'));
const TrustLensPostureL3 = React.lazy(() => import('src/pages/TrustLens/PostureL3Page'));
const TrustLensPostureL4 = React.lazy(() => import('src/pages/TrustLens/PostureL4Page'));
const TrustLensPostureL5 = React.lazy(() => import('src/pages/TrustLens/PostureL5Page'));
const TrustLensDashboard = React.lazy(() => import('src/pages/TrustLens/AssessmentDashboardPage'));
const TrustLensProfile = React.lazy(() => import('src/pages/TrustLens/ProfileContextPage'));
const TrustLensSectionsTeam = React.lazy(() => import('src/pages/TrustLens/SectionsTeamPage'));
const TrustLensReport = React.lazy(() => import('src/pages/TrustLens/ReportPage'));
const TrustLensInvitations = React.lazy(() => import('src/pages/TrustLens/InvitationsPage'));
// Financial Crime (sanctions and third-party screening)
const FcmOverview = React.lazy(() => import('src/pages/FinancialCrime/Overview'));
const FcmSearch = React.lazy(() => import('src/pages/FinancialCrime/Search'));
const FcmSettings = React.lazy(() => import('src/pages/FinancialCrime/Settings'));
const FcmApprovals = React.lazy(() => import('src/pages/FinancialCrime/Approvals'));
const FcmRiskModel = React.lazy(() => import('src/pages/FinancialCrime/RiskModel'));
const FcmCounterparties = React.lazy(() => import('src/pages/FinancialCrime/Counterparties'));
const FcmNewCounterparty = React.lazy(() => import('src/pages/FinancialCrime/NewCounterparty'));
const FcmCounterparty = React.lazy(() => import('src/pages/FinancialCrime/Counterparty'));
const FcmAlerts = React.lazy(() => import('src/pages/FinancialCrime/Alerts'));
const FcmAlert = React.lazy(() => import('src/pages/FinancialCrime/Alert'));
const FcmList = React.lazy(() => import('src/pages/FinancialCrime/ListDetail'));
const FcmTasks = React.lazy(() => import('src/pages/FinancialCrime/Tasks'));
const FcmTask = React.lazy(() => import('src/pages/FinancialCrime/Task'));
const FcmCertificate = React.lazy(() => import('src/pages/FinancialCrime/Certificate'));
const FcmPublicQuestionnaire = React.lazy(() => import('src/pages/FinancialCrime/PublicQuestionnaire'));
// Phase 5 — Sub-Tenant Operations (inter-branch collaboration, branch ops agent)
// const SubTenantInterBranch = React.lazy(() => import('src/pages/SubTenant/InterBranchPage'));  // routes commented out below
// const SubTenantInterBranchNew = React.lazy(() => import('src/pages/SubTenant/InterBranchNewPage'));  // routes commented out below
// const SubTenantInterBranchThread = React.lazy(() => import('src/pages/SubTenant/InterBranchThreadPage'));  // routes commented out below
// const SubTenantBranchOps = React.lazy(() => import('src/pages/SubTenant/BranchOpsPage'));  // routes commented out below
// Playbooks pages not yet available in this build
// const LlmRouting = React.lazy(() => import('src/pages/Tenant/LlmRouting'));  // route commented out
// Admin Partners pages (PartnersDirectory, Scorecard, Onboarding,
// PartnerDetail, PartnerConflicts) removed alongside partnersSeed.ts.
// They were demo-only with no real backend. When platform-admin
// endpoints exist (/api/admin/partners/*), rebuild against them.
// Partner Portal moved to the standalone partner_portal_frontend app — its
// pages (MyEngagements, MyPractitioners, MyComplianceStatus, MyEarnings) and
// /partner-portal/* routes were removed from this customer app.
// PartnerLogin (demo identity switcher) removed — superseded by real auth in
// src/pages/login/login.tsx. Spec moves "Switch Identity" into the header
// dropdown for users with multi-partner membership; tracked as a follow-up.
// const RiskList = React.lazy(() => import('src/pages/Risk/RiskList'));  // route commented out below
// const RiskDetail = React.lazy(() => import('src/pages/Risk/RiskDetail'));  // route commented out below
// const RiskForm = React.lazy(() => import('src/pages/Risk/RiskForm'));  // routes commented out below
const ComplianceLinksDetail = React.lazy(() => import('src/pages/compliance/ComplianceLinksDetail'));
const RoleDashboard = React.lazy(() => import('src/pages/Dashboards/RoleDashboard'));
const CertificationsList = React.lazy(() => import('src/pages/certifications/ListCertifications'));
const CreateQuestion = React.lazy(() => import('src/pages/Surveys_Questions/BankQuestions/CreateQuestion'));
const CreateSurvey = React.lazy(() => import('src/pages/AllSurveys/CreateSurvey'));
const Surveys = React.lazy(() => import('src/pages/Surveys_Questions/Surveys/Surveys'));
const QuestionBankTable = React.lazy(() => import('../../pages/Surveys_Questions/BankQuestions/questionCatalog'));

const UpdateSurvey = React.lazy(() => import('src/pages/AllSurveys/UpdateSurvey'));
const UpdateQuestion = React.lazy(() => import('src/pages/Surveys_Questions/BankQuestions/UpdateQuestion'));
const PermissionPages = React.lazy(() => import('src/pages/PermissionLoginPages/PermissionPages'));
const ImpactAreaTable = React.lazy(() => import('src/pages/ImpactArea/ViewImpact'));
const ReferencesPage = React.lazy(() => import('src/pages/Configuration/ReferencesPage'));
const CreateImpact = React.lazy(() => import('src/pages/ImpactArea/CreateImpact'));
const UpdateImpact = React.lazy(() => import('src/pages/ImpactArea/update/updateImpact'));
const Register = React.lazy(() => import('src/pages/register/Register'));
const CreateFunctionPage = React.lazy(() => import('src/pages/FunctionUser/Function'));
// const ServicesPage = React.lazy(() => import('src/pages/Services/ServicesPage'));  // route commented out

const ReplayQuestion = React.lazy(() => import('src/pages/AllSurveys/ReplayQuestion'));
// const QuestionControlTree = React.lazy(() => import('src/pages/QuestionControl/QuestionControlTree'));  // route commented out
// const TeamOnboarding = React.lazy(() => import('src/pages/invitationUser/inviteTeam'));  // route commented out
const OrganizationChart = React.lazy(() => import('src/pages/OrganizationChart/OrganizationChart'));
const ControlUpdatePage = React.lazy(() => import('src/pages/controls/update/[id]'));
const AssetsList = React.lazy(() => import('src/pages/AssetsManagement/AssetsList'));
const CreateAsset = React.lazy(() => import('src/pages/AssetsManagement/CreateAsset'));
const AssetDetails = React.lazy(() => import('src/pages/AssetsManagement/AssetDetails'));
const EditAsset = React.lazy(() => import('src/pages/AssetsManagement/EditAsset'));
const RemediationList = React.lazy(() => import('src/pages/RemediationManagement/RemediationList'));
const CreateRemediation = React.lazy(() => import('src/pages/RemediationManagement/CreateRemediation'));
const EditRemediation = React.lazy(() => import('src/pages/RemediationManagement/EditRemediation'));
// const DataSourceList = React.lazy(() => import('src/pages/dataresource/index'));  // routes commented out below
// const DataSourceDetails = React.lazy(() => import('src/pages/dataresource/DataSourceDetails'));  // routes commented out below
// const UpdateDataSource = React.lazy(() => import('src/pages/dataresource/update/UpdateDataSource'));  // routes commented out below
const DocumentList = React.lazy(() => import('src/pages/DocumentManagement/DocumentList'));
const DocumentUpload = React.lazy(() => import('src/pages/DocumentManagement/DocumentUpload'));
const DocumentDetails = React.lazy(() => import('src/pages/DocumentManagement/DocumentDetails'));
const DocumentEdit = React.lazy(() => import('src/pages/DocumentManagement/DocumentEdit'));
const DocumentCategoriesPage = React.lazy(() => import('src/pages/DocumentManagement/DocumentCategoriesPage'));
const WorkflowBuilderPage = React.lazy(() => import('src/pages/DocumentManagement/WorkflowBuilderPage'));
// const DocumentApprovalPage = React.lazy(() => import('src/pages/DocumentManagement/DocumentApprovalPage'));
const EntityApprovalPage = React.lazy(() => import('src/pages/workflow/EntityApprovalPage'));
const ServicesStatusPage = React.lazy(() => import('src/pages/Configuration/ServicesStatusPage'));
const LifecycleStagesPage = React.lazy(() => import('src/pages/Configuration/LifecycleStagesPage'));
const DataHandledPage = React.lazy(() => import('src/pages/Configuration/DataHandledPage'));
const CriticalityPage = React.lazy(() => import('src/pages/Configuration/CriticalityPage'));
const AssetTypesPage = React.lazy(() => import('src/pages/Configuration/AssetTypesPage'));
const AssetClassificationsPage = React.lazy(() => import('src/pages/Configuration/AssetClassificationsPage'));
// const TenableIntegrationPage = React.lazy(() => import('src/pages/Configuration/TenableIntegrationPage'));  // route commented out below
// const ComplianceDashboard = React.lazy(() => import('src/pages/DocumentManagement/ComplianceDashboard'));  // route commented out
// const SopMastersPage = React.lazy(() => import('src/pages/sopCollection/SopMastersPage'));
const ServicesCatalogPage = React.lazy(() => import('src/pages/Catalogs/ServicesCatalogPage'));
const ServiceCatalogCreatePage = React.lazy(() => import('src/pages/business-model-inventory/create'));
// const DocumentStructurePage = React.lazy(() => import('src/pages/DocumentManagement/DocumentStructurePage'));  // route commented out
// Legacy /demo-partner/* lazy imports removed — superseded by /partner-portal/*
// per the Partner Portal production spec (Fix 2).

// Partner Portal pages and their React.lazy imports were removed — the Partner
// Portal now lives in the standalone partner_portal_frontend app.
const ControlsIndex = React.lazy(() => import('src/pages/controls/index'));
const CreateControlPage = React.lazy(() => import('src/pages/controls/create'));
const ControlPage = React.lazy(() => import('src/pages/controls/[id]'));
// const ReportingPage = React.lazy(() => import('src/pages/Reporting/ReportingPage'));  // route commented out below
// const CreateSubBusinessUnitPage = React.lazy(() => import('src/pages/Sub-Business-Unit/create'));
// const CreateRootCausePage = React.lazy(() => import('src/pages/rootCauses/create'));
// const CreateRiskCategoryPage = React.lazy(() => import('src/pages/riskCategories/create'));
// const CreateSubFunctionPage = React.lazy(() => import('../../pages/sub_functions/create'));
// const CreateSystemPage = React.lazy(() => import('src/pages/systems/create'));
// const CreateJobTitlePage = React.lazy(() => import('src/pages/jobTitles/create'));
// const CreateDiscoveredTypePage = React.lazy(() => import('src/pages/discoveredTypes/create'));
// const CreateCurrencyPage = React.lazy(() => import('src/pages/currencies/create'));
// const CreateActivityPage = React.lazy(() => import('src/pages/activities/create'));
// const ControlReferencePage = React.lazy(() => import('src/pages/ControlRefrence/create'));
// const StandardsStatusPage = React.lazy(() => import('src/pages/standards/StandardsStatus'));  // route commented out
// const FinancialImpactsPage = React.lazy(() => import('src/pages/financialImpacts/create'));
// const EventTypePage = React.lazy(() => import('src/pages/eventsType/create'));
// const CreateAccountType = React.lazy(() => import('src/pages/AccountType/create'));

// const ActivityLogPage = React.lazy(() => import('../../pages/audit/ActivityLogPage'));  // route commented out
const SiemLogPage = React.lazy(() => import('../../pages/audit/SiemLogPage'));
// SLA — escalation policies + live SLA monitor (backend: new_adib_backend /sla/*)
const SlaTasks = React.lazy(() => import('../../pages/SLA/SlaTasks'));
const SlaWorkflows = React.lazy(() => import('../../pages/SLA/SlaWorkflows'));
const CreateSlaWorkflow = React.lazy(() => import('../../pages/SLA/CreateSlaWorkflow'));

/**
 * Legacy `/compliance/...` deep links.
 *
 * This app used to be served under a `/compliance` base path, so links that predate the
 * move to the root — bookmarks, emailed URLs, anything typed from memory — still carry the
 * prefix. Only `/compliance/login` had a redirect, so every OTHER such URL
 * (`/compliance/sla-tasks`, `/compliance/remediation`, …) matched no route at all: React
 * Router rendered nothing, giving a blank content area inside the shell. Worse, an
 * unmatched route never mounts a ProtectedRoute, so it neither showed the page NOR
 * bounced an expired session to login — it just sat there blank.
 *
 * Stripping the prefix hands the URL to the canonical route, which is what puts it back
 * under ProtectedRoute: a live session renders the page, a removed/expired token redirects
 * to the DCC login (see utils/dccLogin). Search and hash are preserved so query params and
 * anchors survive the hop.
 *
 * Safe as a splat: no canonical route lives under the singular `/compliance/` segment.
 * `/compliances*` and `/compliance-status` are different path segments and do not match.
 */
function RedirectLegacyCompliancePrefix() {
  const location = useLocation();
  const stripped = location.pathname.replace(/^\/compliance(?=\/|$)/, '');
  const target = `${stripped || '/'}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

function RedirectAssetsToResources() {
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const path = location.pathname;
  if (path === '/assets') return <Navigate to="/catalogs/resources" replace />;
  if (path === '/assets/create') return <Navigate to="/catalogs/resources/create" replace />;
  if (path.startsWith('/assets/edit/') && id) return <Navigate to={`/catalogs/resources/edit/${id}`} replace />;
  if (path.startsWith('/assets/') && id) return <Navigate to={`/catalogs/resources/${id}`} replace />;
  return <Navigate to="/catalogs/resources" replace />;
}

const ComplianceModuleDashboard = React.lazy(() => import('../../pages/compliance/ComplianceModuleDashboard'));
// const ConfigurationDashboard = React.lazy(() => import('../../pages/Configuration/ConfigurationDashboard'));

const ViewControl = React.lazy(() => import('../../pages/compliance/assessment/viewControl'));
const CreateCompliance = React.lazy(() => import('../../pages/compliance/createCompliance/CreateCompliance'));
const UpdateCompliance = React.lazy(() => import('../../pages/compliance/editCompliance/UpdateCompliance'));
const Compliance = React.lazy(() => import('../../pages/compliance/compliance/Compliance'));
const InformationCompliance = React.lazy(() => import('../../pages/compliance/info/informationCompliance'));
const References = React.lazy(() => import('../../pages/referenes/references'));
const Login = React.lazy(() => import('../../pages/login/login'));
const LogoutClear = React.lazy(() => import('../../pages/logout-clear/LogoutClear'));

// const Tickets = React.lazy(() => import('../../pages/Tickets/Tickets'));  // route commented out
const Remediations = React.lazy(() => import('../../pages/compliance/remediation/Remediations'));
const RemediationDetail = React.lazy(() => import('../../pages/compliance/remediation/RemediationDetail'));
// Subscribe & Zoho subscribe - commented out
// const SubscriptionPage = React.lazy(() => import('../../pages/subscription/SubscriptionPage'));
// const RenewSubscriptionPage = React.lazy(() => import('../../pages/renew-subscription/RenewSubscriptionPage'));




const Main: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [appLanguage, setAppLanguage] = useState<'en' | 'ar'>(() => (i18n.language === 'ar' ? 'ar' : 'en'));
    const location = useLocation();

    // Header hamburger: on desktop it collapses/expands the sidebar (icon rail
    // ↔ full, matching the reference design); on mobile it opens the drawer.
    const toggleSidebar = () => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            setMobileNavOpen((v) => !v);
        } else {
            setIsSidebarOpen((v) => !v);
        }
    };

    // Close the mobile nav whenever the route changes.
    useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

    // Note: pathname is relative to basename, so '/login' is correct.
    // Both lists live in utils/authPaths - InactivityHandler asks the same question.
    const isLoginPage = isLoginPath(location.pathname);
    const isPublicPage = isPublicPath(location.pathname);

    // App-level session gate.
    //
    // Each route already sits behind ProtectedRoute, but that only governs the CONTENT —
    // the sidebar, header and breadcrumb below render as soon as Main does, before the
    // two-request session check (/csrf/token then /auth/me) has come back. A signed-out
    // visitor therefore watched the app paint around them and only then get thrown out,
    // which is the "page opened, then went to login" flash. Deciding here, above the
    // chrome, means an anonymous visitor sees exactly one screen and never a glimpse of
    // an app they cannot use.
    const [authState, setAuthState] = useState<'checking' | 'authed' | 'anon'>('checking');
    // True once /auth/me has returned a real user in this page load (a public page's
    // 'authed' does not count), false again the moment a check comes back anonymous.
    // While true, a re-check on navigation keeps the chrome (Sidebar/Header) mounted
    // and only swaps the page area for the spinner, so the Sidebar does not unmount
    // and collapse to Home on every page change. Pages still unmount and remount
    // exactly as before. Logout is a full reload, so this never outlives a user.
    const sessionVerifiedRef = useRef(false);
    useEffect(() => {
        if (isPublicPage) { setAuthState('authed'); return; }
        let cancelled = false;
        setAuthState('checking');
        getUserData()
          .then((user) => {
            if (cancelled) return;
            if (!user) rememberAttemptedUrl();
            sessionVerifiedRef.current = !!user;
            setAuthState(user ? 'authed' : 'anon');
          })
          .catch(() => { if (!cancelled) { rememberAttemptedUrl(); sessionVerifiedRef.current = false; setAuthState('anon'); } });
        return () => { cancelled = true; };
    }, [isPublicPage, location.pathname]);

    // A 401 from any request the shell fires lands here rather than navigating away
    // (see utils/sessionEvents) — one listener, one decision, no cross-origin hop that
    // blanks the page mid-render. Public pages ignore it: /login must not replace itself
    // with "not authorized" just because a background call failed.
    useEffect(() => {
        if (isPublicPage) return;
        return onSessionExpired(() => {
            // Capture where they were BEFORE the screen swaps, so signing back in returns
            // them to the page they were working on rather than the app's home.
            rememberAttemptedUrl();
            sessionVerifiedRef.current = false;
            setAuthState('anon');
        });
    }, [isPublicPage]);

    const handleLanguageChange = (lng: string) => {
        setAppLanguage(lng === 'ar' ? 'ar' : 'en');
    };

    useEffect(() => {
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    // Below every hook on purpose: an early return placed above one would change the hook
    // order between renders (Rules of Hooks) and React would throw once the gate resolved.
    // A re-check after the session was already verified: keep the chrome, spin in the
    // page area only (see sessionVerifiedRef above).
    const recheckingSession = !isPublicPage && authState === 'checking' && sessionVerifiedRef.current;
    if (!isPublicPage && authState === 'checking' && !recheckingSession) {
        // Deliberately not a bare <span className="spinner-border" />: that is a
        // Bootstrap-only element with no text, so if this state ever sticks (a hung
        // /csrf/token or /auth/me) the screen is indistinguishable from a crashed, blank
        // app. Inline styles + a caption mean a stall always looks like a stall.
        return (
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ minHeight: '100vh', gap: '1rem', color: '#5b6b66' }}
            >
                <div
                    style={{
                        width: 34,
                        height: 34,
                        border: '3px solid #dbe5e1',
                        borderTopColor: '#1f7c50',
                        borderRadius: '50%',
                        animation: 'comply-auth-spin 0.8s linear infinite',
                    }}
                />
                <div style={{ fontSize: 14 }}>Checking your session…</div>
                <style>{'@keyframes comply-auth-spin{to{transform:rotate(360deg)}}'}</style>
            </div>
        );
    }

    // NOTE: there is deliberately no `if (anon) return <NotAuthorized />` here any more.
    // Short-circuiting above <Routes> meant an anonymous visitor got 401 for EVERY url,
    // including ones the router does not know — so a typo looked like a session problem
    // and the 404 page was unreachable while signed out. <Routes> now always renders:
    // ProtectedRoute answers 401 for real paths, the catch-all `*` answers 404 for the
    // rest, and the chrome below simply stays hidden while anonymous so nothing flashes.
    const showChrome = !isLoginPage && authState !== 'anon';
//console.log(userRole,"userRoleuserRoleuserRole")
    return (
        <div className={`layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            {showChrome &&  <Sidebar
              isOpen={isSidebarOpen}
              onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
              language={appLanguage}
              mobileOpen={mobileNavOpen}
              onMobileClose={() => setMobileNavOpen(false)}
            />}
            <div className={`${showChrome ? 'main-content' : 'main-content-login w-100'}`}>
                {/* Toasts render from the single <ToastContainer/> in App.tsx, not here.
                    `.layout` above is `position: fixed`, which per spec unconditionally
                    creates a stacking context — anything nested inside it, no matter its
                    own z-index, is confined to that context's paint layer and can never
                    out-rank a sibling like a Bootstrap modal (reactstrap portals the modal
                    to a fresh element on `document.body`, outside `.layout` entirely, at
                    z-index 1050). A second ToastContainer mounted here rendered every toast
                    dimmed under any open modal's backdrop; removed rather than z-index-bumped,
                    since bumping it here couldn't have escaped the trap anyway. */}
                {showChrome && (
                  <>
                    {/* Subscribe & Zoho subscribe - commented out
                    <SubscriptionAlert />
                    */}
                    <Header toggleSidebar={toggleSidebar}  className='w-100 z-100' />
                  </>
                )}
                <PageHeadingProvider>
                <div className="d-flex flex-column flex-grow-1 min-h-0 main-content-body" style={{ minHeight: 0 }}>
                {showChrome && (
                  <PageHeadingBar>
                    <div className="main-breadcrumb-bar flex-shrink-0">
                      <div className="main-breadcrumb-bar__inner">
                        <AppHeaderBreadcrumbs className="w-full" />
                        <AppPageHeading />
                      </div>
                    </div>
                  </PageHeadingBar>
                )}
                <div className="main-content-scroll">
                {recheckingSession ? (
                  <div className="d-flex justify-content-center p-5">
                    <span className="spinner-border text-primary" role="status" />
                  </div>
                ) : (
                <React.Suspense fallback={
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                }>
                    <ModuleGate>
                    <FederationGate>
                    <Routes>

                    <Route path="/" element={
                          <ProtectedRoute>
                            <RoleBasedRoute />
                          </ProtectedRoute>
                        } />
                        {/* Financial Crime: the screens, and the questionnaire a counterparty answers without an account (/q/<token>) */}
                        <Route path="/financial-crime" element={<ProtectedRoute><FcmOverview /></ProtectedRoute>} />
                        <Route path="/financial-crime/search" element={<ProtectedRoute><FcmSearch /></ProtectedRoute>} />
                        <Route path="/financial-crime/settings" element={<ProtectedRoute><FcmSettings /></ProtectedRoute>} />
                        <Route path="/financial-crime/approvals" element={<ProtectedRoute><FcmApprovals /></ProtectedRoute>} />
                        <Route path="/financial-crime/risk-model" element={<ProtectedRoute><FcmRiskModel /></ProtectedRoute>} />
                        <Route path="/financial-crime/counterparties" element={<ProtectedRoute><FcmCounterparties /></ProtectedRoute>} />
                        <Route path="/financial-crime/counterparties/new" element={<ProtectedRoute><FcmNewCounterparty /></ProtectedRoute>} />
                        <Route path="/financial-crime/counterparties/:id" element={<ProtectedRoute><FcmCounterparty /></ProtectedRoute>} />
                        <Route path="/financial-crime/alerts" element={<ProtectedRoute><FcmAlerts /></ProtectedRoute>} />
                        <Route path="/financial-crime/alerts/:id" element={<ProtectedRoute><FcmAlert /></ProtectedRoute>} />
                        <Route path="/financial-crime/lists/:id" element={<ProtectedRoute><FcmList /></ProtectedRoute>} />
                        <Route path="/financial-crime/tasks" element={<ProtectedRoute><FcmTasks /></ProtectedRoute>} />
                        <Route path="/financial-crime/tasks/:id" element={<ProtectedRoute><FcmTask /></ProtectedRoute>} />
                        <Route path="/financial-crime/certificates/:code" element={<ProtectedRoute><FcmCertificate /></ProtectedRoute>} />
                        <Route path="/q/:token" element={<FcmPublicQuestionnaire />} />
                        {/* Entry point used by the GRC home "Cyber Security Compliance" card */}
                        <Route path="/cyber-security" element={
                          <ProtectedRoute>
                            <RoleBasedRoute />
                          </ProtectedRoute>
                        } />
                        {/* Reporting System lives in the ORM app, not here.
                        <Route path="/reporting" element={
                          <ProtectedRoute>
                            <ReportingPage />
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/info" element={
                          <ProtectedRoute>
                            <InformationCompliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/dashboard" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <ComplianceModuleDashboard />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances" element={
                          <ProtectedRoute>
                            <Compliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/standards" element={
                          <ProtectedRoute>
                            <References />
                          </ProtectedRoute>
                        } />
                        {/* disabled on request
                        <Route path="/compliance-status" element={
                          <ProtectedRoute>
                            <ComplianceStatus />
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/remediation" element={
                          <ProtectedRoute>
                            <Remediations />
                          </ProtectedRoute>
                        } />
                        <Route path="/remediation/:id" element={
                          <ProtectedRoute>
                            <RemediationDetail />
                          </ProtectedRoute>
                        } />
                        {/* disabled on request
                        <Route path="/ticket" element={
                          <ProtectedRoute>
                            <Tickets />
                          </ProtectedRoute>
                        } /> */}
    <Route path="/organization-chart" element={
                          <ProtectedRoute>
                            <OrganizationChart/>
                          </ProtectedRoute>
                        } /> 

                        <Route path="/create" element={
                          <ProtectedRoute>
                            <CreateCompliance />
                          </ProtectedRoute>
                        } />
                          <Route path="/catalogs/services" element={
                          <ProtectedRoute>
                            <ServicesCatalogPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/services/create" element={
                          <ProtectedRoute>
                            <ServiceCatalogCreatePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/services/update/:id" element={
                          <ProtectedRoute>
                            <ServiceCatalogCreatePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/edit/:id" element={
                          <ProtectedRoute>
                            <UpdateCompliance />
                          </ProtectedRoute>
                        } />
                        <Route path="/certifications" element={
                          <ProtectedRoute>
                            <CertificationsList/>
                          </ProtectedRoute>
                        } />

                        <Route path="/compliances/status/:id" element={
                          <ProtectedRoute>
                            <ComplianceStatus/>
                          </ProtectedRoute>
                        } />
                        <Route path="/compliances/view-control/:id/:fieldId/:subFieldId" element={
                          <ProtectedRoute>
                            <ViewControl />
                          </ProtectedRoute>
                        } />
                        <Route path="/login" element={<Login />} />
                        <Route path="/logout-clear" element={<LogoutClear />} />
                        {/* Legacy /compliance/login URL: redirect to canonical /login.
                            Kept explicit even though the splat below covers it — a static
                            segment outranks a splat in React Router v6, so this still wins
                            and the login hop stays obvious when reading the routes. */}
                        <Route path="/compliance/login" element={<Navigate to="/login" replace />} />
                        {/* Every other legacy /compliance/* deep link → its canonical path. */}
                        <Route path="/compliance/*" element={<RedirectLegacyCompliancePrefix />} />
                        <Route path="/register" element={<Register />} />
                        {/* Partner Portal routes removed — moved to the standalone partner_portal_frontend app. */}
                        {/* Pianat Admin routes moved to the standalone pianat_admin_frontend app. */}
                        {/* Phase 5 — Sub-Tenant Operations. Sidebar shows these
                            only for tenants with a parent (tenantDepth > 0);
                            backend enforces participant-based access per row. */}
                        {/* Sub-Tenant Operations routes disabled on request.
                        <Route path="/sub-tenant/inter-branch" element={
                          <ProtectedRoute><SubTenantInterBranch /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/inter-branch/new" element={
                          <ProtectedRoute><SubTenantInterBranchNew /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/inter-branch/:id" element={
                          <ProtectedRoute><SubTenantInterBranchThread /></ProtectedRoute>
                        } />
                        <Route path="/sub-tenant/branch-ops-agent" element={
                          <ProtectedRoute><SubTenantBranchOps /></ProtectedRoute>
                        } /> */}
                        {/* Phase 4 — Tenant Owner Admin. Backend gate is
                            @TenantOwnerOnly (canManageMemberships + subtree
                            scope); the sidebar hides these for everyone else. */}
                        <Route path="/function" element={
                          <ProtectedRoute>
                            <CreateFunctionPage/>
                          </ProtectedRoute>
                        } />
                        {/* disabled on request
                        <Route path="/services" element={
                          <ProtectedRoute>
                            <ServicesPage/>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/document-categories" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <DocumentCategoriesPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/workflows" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <WorkflowBuilderPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* <Route path="/document-approval" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <DocumentApprovalPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/approvals/:entityType/:entityId" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <EntityApprovalPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* <Route path="/tenants/tickets" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantTickets/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* <Route path="/tenants/settings" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantSettings/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* <Route path="/tenants/standards" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantStandards/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* <Route path="/tenants/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <TenantDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* disabled on request
                        <Route path="/llm-routing" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <LlmRouting/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* Admin Partners routes removed — see lazy-import block above. */}
                        {/* Partner Portal /partner-portal/* routes (engagements, earnings,
                            practitioners, status) removed — moved to the standalone
                            partner_portal_frontend app. */}
                        {/* Risk list route disabled on request.
                        <Route path="/risks" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskList/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* Risk create route disabled on request.
                        <Route path="/risks/create" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskForm/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* Risk detail route disabled on request.
                        <Route path="/risks/:id" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* Risk edit route disabled on request.
                        <Route path="/risks/:id/edit" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RiskForm/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/compliances/:id/links" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ComplianceLinksDetail/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboards/roles" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <RoleDashboard/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* <Route path="/configuration/dashboard" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <ConfigurationDashboard/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/configuration/services-status" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <ServicesStatusPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/lifecycle-stages" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <LifecycleStagesPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/data-handled" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <DataHandledPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/criticalities" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <CriticalityPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/asset-types" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <AssetTypesPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        <Route path="/configuration/asset-classifications" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <AssetClassificationsPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Tenable health view route disabled on request.
                        <Route path="/configuration/tenable" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <TenableIntegrationPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        {/* /tenable-vulnerabilities is gone: the browser was retired and its
                            export lives on Configuration -> Integration Settings. */}
                        {/* One screen holding both integrations; each section gates itself on its own
                            permission page (Tenable / Laserfiche Integration). */}
                        <Route path="/configuration/integrations" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <IntegrationSettingsPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                        {/* Every Excel data sheet in one list; each row gates itself on the
                            page that owns that sheet. */}
                        <Route path="/configuration/data-sheets" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <DataSheetsPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        
                        {/* <Route path="/catalogs/sop-masters" element={
                          <ProtectedRoute>
                            <SopMastersPage />
                          </ProtectedRoute>
                        } /> */}
                        {/* disabled on request
                        <Route path="/QuestionControlTree" element={
                          <ProtectedRoute>
                            <QuestionControlTree/>
                          </ProtectedRoute>
                        } /> */}
                   <Route path="/viewsurvey" element={
                     <ProtectedRoute>
                       <Surveys/>
                     </ProtectedRoute>
                   } /> 
                      <Route path="/updatesurvey/:id" element={
                        <ProtectedRoute>
                          <UpdateSurvey/>
                        </ProtectedRoute>
                      } />
                      {/* disabled on request
                      <Route path="/updateSurvey/:id" element={
                        <ProtectedRoute>
                          <UpdateSurvey/>
                        </ProtectedRoute>
                      } /> */}
                        {/* disabled on request
                        <Route path="/control" element={
                        <ProtectedRoute>
                          <ControlsIndex/>
                        </ProtectedRoute>
                      } /> */}
                        {/* disabled on request
                        <Route path="/standards-status" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>}>
                              <StandardsStatusPage/>
                            </React.Suspense>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/controls" element={
                        <ProtectedRoute>
                          <ControlsIndex/>
                        </ProtectedRoute>
                      } />
                        <Route path="/controls/create" element={
                        <ProtectedRoute>
                          <CreateControlPage/>
                        </ProtectedRoute>
                      } />
                        <Route path="/controls/:id" element={
                        <ProtectedRoute>
                          <ControlPage/>
                        </ProtectedRoute>
                      } />
                        <Route path="/user" element={
                          <ProtectedRoute>
                            <UserManage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/user/activation/:id" element={
                          <ProtectedRoute>
                            <UserActivation/>
                          </ProtectedRoute>
                        } />
                        <Route path="/user/permissions" element={
                          <ProtectedRoute>
                            <UserPermissionManage/>
                          </ProtectedRoute>
                        } /> 
                        <Route path="/permission/groups" element={
                          <ProtectedRoute>
                            <PermissionGroupPageManage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/permission/groups-pages" element={<Navigate to="/permission/groups" replace />} />
                        <Route path="/notification" element={
                          <ProtectedRoute>
                            <Notification/>
                          </ProtectedRoute>
                        } /> 
           {/* disabled on request
           <Route path="/team-onboarding" element={
             <ProtectedRoute>
               <TeamOnboarding />
             </ProtectedRoute>
           } /> */}

                        {/* disabled on request
                        <Route path="/exportsurvey/:id" element={
                          <ProtectedRoute>
                            <ExportSurvey/>
                          </ProtectedRoute>
                        } />  */}
                        {/* disabled on request
                        <Route path="/assessmentsurvey/:id" element={
                          <ProtectedRoute>
                            <ReplayQuestion/>
                          </ProtectedRoute>
                        } />  */}



{/* disabled on request
<Route path="/SurveyCategoryQuestionManager" element={
                            <ProtectedRoute>
                                <SurveyCategoryQuestionManager/>
                            </ProtectedRoute>
                        } /> */}
 {/* disabled on request
 <Route path="/questionassessment" element={
                            <ProtectedRoute>
                                <ViewQuestionsTable/>
                            </ProtectedRoute>
                        } />  */}

{/* disabled on request
<Route path="/ctreatQuestionAssessment" element={
                            <ProtectedRoute>
                                <CreateQuestionForm/>
                            </ProtectedRoute>
                        } />  */}




                        <Route path="/assessment-survey/:id" element={<ReplayQuestion/>} /> 

      <Route path="/updateQuestion/:id" element={<UpdateQuestion/>} />
                           <Route path="/createQuestion" element={<CreateQuestion/>} /> 
                        <Route path="/create-survey" element={<CreateSurvey/>} /> 
                        <Route path="/export-survey/:id" element={<ExportSurvey/>} /> 
                  
                          <Route path="/surveys" element={<Surveys/>} /> 
                        <Route path="/bankQuestions" element={<QuestionBankTable/>} /> 

                     
                        <Route path="/controls/update/:id" element={
                            <ControlUpdatePage/>
                        } />

                        {/* disabled on request
                        <Route path="/updatecontrol/:id" element={
                            <ControlUpdatePage/>
                        } /> */}

                        {/* Task calendar route disabled on request.
                        <Route path="/viewTaskCalender" element={
                          <ProtectedRoute>
                            <TaskUserCalender/>
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/permission" element={
                          <ProtectedRoute>
                            <PermissionPages/>
                          </ProtectedRoute>
                        } />
                        {/* Subscribe & Zoho subscribe - commented out
                        <Route path="/subscription" element={
                          <ProtectedRoute>
                            <SubscriptionPage/>
                          </ProtectedRoute>
                        } />
                        <Route path="/renew-subscription" element={
                          <ProtectedRoute>
                            <RenewSubscriptionPage/>
                          </ProtectedRoute>
                        } />
                        */}
                        {/* Resources (formerly Assets) under Catalog */}
                        <Route path="/catalogs/resources" element={
                          <ProtectedRoute>
                            <AssetsList />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/create" element={
                          <ProtectedRoute>
                            <CreateAsset />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/:id" element={
                          <ProtectedRoute>
                            <AssetDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/catalogs/resources/edit/:id" element={
                          <ProtectedRoute>
                            <EditAsset />
                          </ProtectedRoute>
                        } />
                        <Route path="/remediation" element={
                          <ProtectedRoute>
                            <RemediationList />
                          </ProtectedRoute>
                        } />
                        <Route path="/remediation/create" element={
                          <ProtectedRoute>
                            <CreateRemediation />
                          </ProtectedRoute>
                        } />
                        <Route path="/remediation/edit/:id" element={
                          <ProtectedRoute>
                            <EditRemediation />
                          </ProtectedRoute>
                        } />
                        {/* Redirect legacy /assets paths to /catalogs/resources */}
                        {/* <Route path="/assets/create" element={<Navigate to="/catalogs/resources/create" replace />} /> */}
                        {/* <Route path="/assets/edit/:id" element={<ProtectedRoute><RedirectAssetsToResources /></ProtectedRoute>} /> */}
                        {/* <Route path="/assets/:id" element={<ProtectedRoute><RedirectAssetsToResources /></ProtectedRoute>} /> */}
                        {/* <Route path="/assets" element={<Navigate to="/catalogs/resources" replace />} /> */}

                           <Route path="/impactarea" element={
                          <ProtectedRoute>
                          <ImpactAreaTable />
                          </ProtectedRoute>
                        } />
                        {/* Settings → References: the lookup behind a control's Reference select */}
                        <Route path="/configuration/references" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <ReferencesPage />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />
                         {/* disabled on request
                         <Route path="/rcsa-compliance" element={
                          <ProtectedRoute>
                            <RcsaCatalogCompliance />
                          </ProtectedRoute>
                        } /> */}
                        <Route path="/rcsa" element={
                          <ProtectedRoute>
                            <Rcsa />
                          </ProtectedRoute>
                        } />

{/* disabled on request
<Route path="/standardsSupCompliance" element={
                          <ProtectedRoute>
                            <StandardsSupCompliance />
                          </ProtectedRoute>
                        } /> */}
                        {/* disabled on request
                        <Route path="/certificationsSup" element={
                          <ProtectedRoute>
                            <CertificationsListSup />
                          </ProtectedRoute>
                        } /> */}







                        {/* Policy / SOP Engine (from process project) */}
                        {/* disabled on request
                        <Route path="/audit/activity-log" element={
                          <ProtectedRoute>
                            <ActivityLogPage />
                          </ProtectedRoute>
                        } /> */}
                        {/* The same audit history, read through the filter the server actually implements
                            (GET /target), with search, sorting and paging done here because the endpoint
                            offers none. */}
                        <Route path="/audit/siem-log" element={
                          <ProtectedRoute>
                            <React.Suspense fallback={<div className="d-flex justify-content-center p-5"><span className="spinner-border text-success" /></div>}>
                              <SiemLogPage />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* User Guide (from process project) */}

                        {/* Data Source (dataresource) routes disabled on request.
                        <Route path="/dataresource" element={
                          <ProtectedRoute>
                            <DataSourceList />
                          </ProtectedRoute>
                        } />
                        <Route path="/dataresource/:id" element={
                          <ProtectedRoute>
                            <DataSourceDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/dataresource/update/:id" element={
                          <ProtectedRoute>
                            <UpdateDataSource />
                          </ProtectedRoute>
                        } /> */}
                        {/* Document Management Routes */}
                        <Route path="/documents" element={
                          <ProtectedRoute>
                            <DocumentList />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/upload" element={
                          <ProtectedRoute>
                            <DocumentUpload />
                          </ProtectedRoute>
                        } />
                        <Route path="/documents/:id/edit" element={
                          <ProtectedRoute>
                            <DocumentEdit />
                          </ProtectedRoute>
                        } />

                             {/* <Route path="/subBusinessUnit" element={
                          <ProtectedRoute>
                            <CreateSubBusinessUnitPage/>
                          </ProtectedRoute>
                        } />



                      <Route path="/jobTitle" element={
                          <ProtectedRoute>
                            <CreateJobTitlePage/>
                          </ProtectedRoute>
                        } />

<Route path="/createCurrency" element={
                          <ProtectedRoute>
                            <CreateCurrencyPage/>
                          </ProtectedRoute>
                        } />

                     <Route path="/discoveredTypes" element={
                          <ProtectedRoute>
                            <CreateDiscoveredTypePage/>
                          </ProtectedRoute>
                        } />

                       <Route path="/rootCause" element={
                          <ProtectedRoute>
                            <CreateRootCausePage/>
                          </ProtectedRoute>
                        } />

                     <Route path="/system" element={
                          <ProtectedRoute>
                            <CreateSystemPage/>
                          </ProtectedRoute>
                        } />

    
<Route path="/subFunction" element={
                          <ProtectedRoute>
                            <CreateSubFunctionPage/>
                          </ProtectedRoute>
                        } /> */}
{/*     
    <Route path="/activities" element={
                          <ProtectedRoute>
                            <CreateActivityPage/>
                          </ProtectedRoute>
                        } /> */}
                        
{/* <Route path="/riskCategory" element={
                          <ProtectedRoute>
                            <CreateRiskCategoryPage/>
                          </ProtectedRoute>
                        } /> */}
      
      {/* <Route path="/controlReference" element={
                          <ProtectedRoute>
                            <ControlReferencePage/>
                          </ProtectedRoute>
                        } /> */}

{/* <Route path="/accountType" element={
                          <ProtectedRoute>
                            <CreateAccountType/>
                          </ProtectedRoute>
                        } /> */}
                        
      {/* <Route path="/eventType" element={
                          <ProtectedRoute>
                            <EventTypePage/>
                          </ProtectedRoute>
                        } /> */}

{/* 

<Route path="/financialImpacts" element={
                          <ProtectedRoute>
                            <FinancialImpactsPage/>
                          </ProtectedRoute>
                        } /> */}

                        <Route path="/documents/:id" element={
                          <ProtectedRoute>
                            <DocumentDetails />
                          </ProtectedRoute>
                        } />
                        {/* disabled on request
                        <Route path="/documents/dashboard" element={
                          <ProtectedRoute>
                            <ComplianceDashboard />
                          </ProtectedRoute>
                        } /> */}
                        {/* disabled on request
                        <Route path="/documents/notifications" element={
                          <ProtectedRoute>
                            <Notification />
                          </ProtectedRoute>
                        } /> */}


{/* disabled on request
<Route path="/ticketsSupCompliance" element={
                          <ProtectedRoute>
                          <TicketsSupCompliance/>
                          </ProtectedRoute>
                        } /> */}







<Route path="/createImpact" element={<CreateImpact/>} /> 
                        <Route path="/updateImpacted/:id" element={<UpdateImpact/>} /> 

                        {/* disabled on request
                        <Route path="/documents/structure" element={
                          <ProtectedRoute>
                            <DocumentStructurePage />
                          </ProtectedRoute>
                        } /> */}

                        {/* SLA — escalation monitor + policy workflows */}
                        <Route path="/sla-tasks" element={
                          <ProtectedRoute>
                            <SlaTasks />
                          </ProtectedRoute>
                        } />
                        <Route path="/sla-workflows" element={
                          <ProtectedRoute>
                            <SlaWorkflows />
                          </ProtectedRoute>
                        } />
                        <Route path="/sla-workflows/create" element={
                          <ProtectedRoute>
                            <CreateSlaWorkflow />
                          </ProtectedRoute>
                        } />
                        <Route path="/sla-workflows/edit/:id" element={
                          <ProtectedRoute>
                            <CreateSlaWorkflow />
                          </ProtectedRoute>
                        } />
                        {/*<Route path="/impactArea" element={<ImpactAreaTable/>} /> 
                        <Route path="/createImpact" element={<CreateImpact/>} /> 
                        <Route path="/updateImpacted/:id" element={<UpdateImpact/>} /> 


                        <Route path="/assessment-survey/:id" element={<ReplayQuestion/>} /> 
                        <Route path="/updateSurvey/:id" element={<UpdateSurvey/>} /> */}





                        

                        {/* TrustLens — light-tier onboarding + Posture Cascade. */}
                        <Route path="/trustlens" element={<Navigate to="/trustlens/dashboard" replace />} />
                        <Route path="/trustlens/posture" element={<Navigate to="/trustlens/posture/l1" replace />} />
                        <Route element={<TrustLensPostureLayout />}>
                          <Route path="/trustlens/posture/l1" element={
                            <ProtectedRoute><TrustLensPostureL1 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l2" element={
                            <ProtectedRoute><TrustLensPostureL2 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l3" element={
                            <ProtectedRoute><TrustLensPostureL3 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l4" element={
                            <ProtectedRoute><TrustLensPostureL4 /></ProtectedRoute>
                          } />
                          <Route path="/trustlens/posture/l5" element={
                            <ProtectedRoute><TrustLensPostureL5 /></ProtectedRoute>
                          } />
                        </Route>
                        <Route path="/trustlens/dashboard" element={
                          <ProtectedRoute><TrustLensDashboard /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/profile" element={
                          <ProtectedRoute><TrustLensProfile /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/assessment" element={
                          <ProtectedRoute><TrustLensSectionsTeam /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/report" element={
                          <ProtectedRoute><TrustLensReport /></ProtectedRoute>
                        } />
                        <Route path="/trustlens/invitations" element={
                          <ProtectedRoute><TrustLensInvitations /></ProtectedRoute>
                        } />

                        {/* ---------------------------------------------------------------
                            404 — MUST STAY THE LAST CHILD OF <Routes>.
                            React Router v6 ranks matches by specificity rather than source
                            order, so a splat cannot literally shadow a real path; keeping it
                            last is for the humans adding routes after it. Add new <Route>s
                            ABOVE this line.

                            Deliberately NOT wrapped in <ProtectedRoute>: a URL that does not
                            exist does not exist whether or not you are signed in. Gating it
                            would turn every unknown path into a 401 for anonymous visitors,
                            which is the 404/auth conflation this split exists to end.
                            --------------------------------------------------------------- */}
                        <Route path="*" element={<NotFound />} />

                    </Routes>
                    </FederationGate>
                    </ModuleGate>
                </React.Suspense>
                )}
                </div>
                </div>
                </PageHeadingProvider>
                {showChrome && <AppFooter />}
            </div>
        </div>
    );
};

export default Main;















